import React, { useContext } from 'react';
import { FormContext } from '../../context/EquipmentFormContext';
import { LuCalendarDays } from 'react-icons/lu';
import { FiCalendar } from 'react-icons/fi';

// ============================================================
// DROPDOWN OPTIONS
// ============================================================

const patFrequencyOptions = [
  'Monthly',
  'Quarterly',
  'Every 6 Months',
  'Annual',
  'Every 2 Years',
  'Every 3 Years',
];

const testResultOptions = [
  'Pass',
  'Pass with Observations',
  'Fail',
  'Not Tested',
  'Pending',
];

export default function Step6() {
  const { formData, updateField } = useContext(FormContext);

  return (
    <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">

      {/* ========================================================
          HEADER
      ======================================================== */}

      <div className="flex items-start space-x-4 mb-6">

        <div className="p-2 bg-blue-600 rounded-lg text-white">
          <LuCalendarDays className="w-5 h-5 stroke-[2.5]" />
        </div>

        <div>

          <div className="flex items-center space-x-2">

            <span className="text-xs font-bold bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
              6
            </span>

            <h2 className="text-base font-bold text-indigo-950">
              PAT / Electrical Testing
            </h2>

          </div>

          <p className="text-xs text-slate-500 mt-0.5">
            Only complete if this equipment requires PAT or electrical testing.
          </p>

        </div>

      </div>

      {/* ========================================================
          REQUIRES PAT
      ======================================================== */}

      <div className="mb-4">

        <label className="block text-xs font-semibold text-slate-700 mb-2">
          Requires PAT?{' '}
          <span className="text-red-500">*</span>
        </label>

        <div className="flex items-center space-x-4 h-9">

          <label className="flex items-center text-sm font-semibold text-slate-800 cursor-pointer">

            <input
              type="radio"
              name="pat_required"
              checked={formData.requiresPat === 'Yes'}
              onChange={() =>
                updateField(
                  'requiresPat',
                  'Yes'
                )
              }
              className="w-4 h-4 text-blue-600 border-slate-300"
            />

            <span className="ml-2">
              Yes
            </span>

          </label>

          <label className="flex items-center text-sm font-semibold text-slate-800 cursor-pointer">

            <input
              type="radio"
              name="pat_required"
              checked={formData.requiresPat === 'No'}
              onChange={() =>
                updateField(
                  'requiresPat',
                  'No'
                )
              }
              className="w-4 h-4 text-blue-600 border-slate-300"
            />

            <span className="ml-2">
              No
            </span>

          </label>

        </div>

      </div>

      {/* ========================================================
          PAT DETAILS
      ======================================================== */}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

        {/* ======================================================
            PAT FREQUENCY
        ====================================================== */}

        <div>

          <label className="block text-xs font-semibold text-slate-700 mb-1">
            PAT Test Frequency{' '}
            <span className="text-red-500">*</span>
          </label>

          <select
            value={formData.patFrequency}
            onChange={(e) =>
              updateField(
                'patFrequency',
                e.target.value
              )
            }
            disabled={formData.requiresPat === 'No'}
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-400"
          >

            <option value="">
              Select frequency
            </option>

            {patFrequencyOptions.map((frequency) => (
              <option
                key={frequency}
                value={frequency}
              >
                {frequency}
              </option>
            ))}

          </select>

        </div>

        {/* ======================================================
            LAST PAT DATE
        ====================================================== */}

        <div>

          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Last PAT Test Date
          </label>

          <div className="relative">

            <input
              type="text"
              value={formData.lastPatDate}
              onChange={(e) =>
                updateField(
                  'lastPatDate',
                  e.target.value
                )
              }
              disabled={formData.requiresPat === 'No'}
              placeholder="DD MMM YYYY"
              className="w-full text-sm border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-400"
            />

            <FiCalendar className="w-4 h-4 text-slate-400 absolute left-3 top-3" />

          </div>

        </div>

        {/* ======================================================
            NEXT PAT DATE
        ====================================================== */}

        <div>

          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Next PAT Due Date
          </label>

          <div className="relative">

            <input
              type="text"
              value={formData.nextPatDue}
              onChange={(e) =>
                updateField(
                  'nextPatDue',
                  e.target.value
                )
              }
              disabled={formData.requiresPat === 'No'}
              placeholder="DD MMM YYYY"
              className="w-full text-sm border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-400"
            />

            <FiCalendar className="w-4 h-4 text-slate-400 absolute left-3 top-3" />

          </div>

        </div>

        {/* ======================================================
            LAST TEST RESULT
        ====================================================== */}

        <div>

          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Last Test Result
          </label>

          <select
            value={formData.lastTestResult}
            onChange={(e) =>
              updateField(
                'lastTestResult',
                e.target.value
              )
            }
            disabled={formData.requiresPat === 'No'}
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-400"
          >

            <option value="">
              Select result
            </option>

            {testResultOptions.map((result) => (
              <option
                key={result}
                value={result}
              >
                {result}
              </option>
            ))}

          </select>

        </div>

      </div>

    </div>
  );
}